import { useEffect, useMemo, useRef } from 'react';

import { getWindow } from '@change-corgi/core/window';

import { useIsVenmoQREnabled, usePaypal } from 'src/app/shared/hooks/payments';
import { isLoaded } from 'src/app/shared/utils/async';
import type { PaymentMethodSaveOptions, VenmoClient } from 'src/app/shared/utils/payments';

export type Props = {
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	savePaymentMethodTemporarily?: boolean;
};

export function useInitializeVenmoInstance({
	paymentMethodSaveOptions,
	savePaymentMethodTemporarily,
}: Props): React.MutableRefObject<VenmoClient | null> {
	const paypalState = usePaypal();

	const venmoRef = useRef<VenmoClient | null>(null);
	const isVenmoQrEnabled = useIsVenmoQREnabled();

	const paymentMethodUsage = useMemo(() => {
		return paymentMethodSaveOptions.shouldSavePaymentMethod || savePaymentMethodTemporarily
			? 'multi_use'
			: 'single_use';
	}, [paymentMethodSaveOptions.shouldSavePaymentMethod, savePaymentMethodTemporarily]);

	useEffect(() => {
		async function init() {
			if (isLoaded(paypalState) && venmoRef.current === null) {
				venmoRef.current = await getWindow().braintree.venmo.create({
					client: paypalState.braintreeClientInstance,
					paymentMethodUsage,
					allowDesktop: isVenmoQrEnabled,
					allowNewBrowserTab: false,
				});
			}
		}
		void init();
		return () => {
			if (venmoRef.current) {
				void venmoRef.current.teardown();
				venmoRef.current = null;
			}
		};
	}, [paypalState, paymentMethodUsage, isVenmoQrEnabled]);

	return venmoRef;
}
